<script setup lang="ts" generic="TData, TValue">
    import type {
        ColumnDef,
        SortingState,
        ColumnFiltersState,
        VisibilityState,
        ExpandedState,
        Column,
        PaginationState,
        RowSelectionState,
        GroupingState,
        Row,
        Table as TableType,
    } from '@tanstack/vue-table';
    import {
        FlexRender,
        getCoreRowModel,
        useVueTable,
        getPaginationRowModel,
        getSortedRowModel,
        getFilteredRowModel,
        getExpandedRowModel,
        getGroupedRowModel,
        getFacetedRowModel,
        getFacetedUniqueValues,
        getFacetedMinMaxValues,
    } from '@tanstack/vue-table';
    import {
        Table,
        TableBody,
        TableCell,
        TableHead,
        TableHeader,
        TableRow,
    } from '@/components/ui/table';
    import { useVirtualizer } from '@tanstack/vue-virtual';
    import { Checkbox } from '@/components/ui/checkbox';
    import { cn } from '@/lib/utils';
    import { Separator } from '@/components/ui/separator';
    import { useFilter } from 'reka-ui';
    import { valueUpdater } from '@/lib/utils';
    import { ref, computed, onMounted, watch, nextTick } from 'vue';
    import type { ComponentPublicInstance } from 'vue';
    import { useUrlSearch } from '@/composables/useUrlSearch';
    import DataTableGroupToggle from '@/components/datatable/DataTableGroupToggle.vue';
    import DataTableOptions from '@/components/datatable/DataTableOptions.vue';
    import DataTableFilters from '@/components/datatable/DataTableFilters.vue';
    import DataTableExportModel from '@/components/datatable/DataTableExportModel.vue';
    import DebouncedInput from '@/components/DebouncedInput.vue';
    import Icon from '@/components/Icon.vue';
    import Loading from '@/components/Loading.vue';
    import {
        booleanFilterFn,
        valueInArrayFilterFn,
        dateRangeFilterFn,
        periodRangeFilterFn,
    } from '@/utils/filterFunctions';

    const props = withDefaults(
        defineProps<{
            tableKey?: string;
            columns: ColumnDef<TData, TValue>[];
            data: TData[];
            loading?: boolean;
            isError?: boolean;
            pinnedColumns?: {
                left?: string[];
                right?: string[];
            };
            defaultSorting?: SortingState;
            defaultPagination?: PaginationState;
            defaultGrouping?: GroupingState;
            defaultColumnVisibility?: VisibilityState;
            defaultColumnFilters?: ColumnFiltersState;
            defaultGlobalFilter?: string;
            autoUrlSearch?: boolean;
            manualPagination?: boolean;
            manualSorting?: boolean;
            manualFiltering?: boolean;
            totalRecords?: number;
            hideControls?: boolean;
            enableSearch?: boolean;
            enableFilters?: boolean;
            enableExport?: boolean;
            enableToolbar?: boolean;
            toolbarClass?: string;
            meta?: Record<string, any>;
            virtualize?: boolean;
            virtualRowHeight?: number;
            virtualOverscan?: number;
            fixedColumnSizes?: Record<string, number>;
            autoComputeColumnSizes?: Record<string, number>; // columnId -> buffer px
            hydrating?: boolean;
        }>(),
        {
            data: () => [],
            pinnedColumns: () => ({
                left: [],
                right: [],
            }),
            defaultSorting: () => [
                {
                    id: 'id',
                    desc: false,
                },
            ],
            defaultPagination: () => ({
                pageIndex: 0,
                pageSize: 20,
            }),
            defaultGrouping: () => [],
            defaultColumnVisibility: () => ({}),
            defaultColumnFilters: () => [],
            defaultGlobalFilter: '',
            autoUrlSearch: false,
            manualPagination: false,
            manualSorting: false,
            manualFiltering: false,
            hideControls: false,
            enableSearch: true,
            enableFilters: true,
            enableExport: true,
            enableToolbar: true,
            meta: () => ({}),
            fixedColumnSizes: () => ({}),
            autoComputeColumnSizes: () => ({}),
            hydrating: false,
        },
    );

    const emit = defineEmits<{
        'pagination-change': [pagination: PaginationState];
        'sorting-change': [sorting: SortingState];
        'filters-change': [filters: ColumnFiltersState];
        'global-filter-change': [value: string];
    }>();

    const STORAGE_KEY = `datatable:filters:${props.tableKey}`;

    // Auto URL search handling
    const { extractAndCleanSearch } = useUrlSearch();

    // Compute the initial global filter value
    const computedDefaultGlobalFilter = computed(() => {
        if (props.autoUrlSearch) {
            const urlSearch = extractAndCleanSearch();
            return urlSearch || props.defaultGlobalFilter;
        }
        return props.defaultGlobalFilter;
    });

    const sorting = ref<SortingState>(props.defaultSorting);

    const columnPinning = ref({
        left: props.pinnedColumns.left,
        right: props.pinnedColumns.right,
    });

    const columnFilters = ref<ColumnFiltersState>(props.defaultColumnFilters);
    const columnVisibility = ref<VisibilityState>(props.defaultColumnVisibility);
    const rowSelection = ref<RowSelectionState>({});
    const expanded = ref<ExpandedState>({});
    const globalFilter = ref(computedDefaultGlobalFilter.value);
    const pagination = ref<PaginationState>(props.defaultPagination);
    const grouping = ref<GroupingState>(props.defaultGrouping);

    const getPinningClasses = (column: Column<TData, any>, isHeader: boolean, depth?: number) => {
        const isPinned = column.getIsPinned();
        const isLeftPinnedColumn = isPinned === 'left';
        const isLastLeftPinnedColumn = isLeftPinnedColumn && column.getIsLastColumn('left');
        const isFirstHeaderColumn =
            isPinned && isHeader && table.getHeaderGroups().length > 1 && depth === 0;

        return {
            'sticky left-0 bg-white inset-shadow-[0_-1px_0_0_var(--table-border)] z-20':
                isLeftPinnedColumn,
            'z-30': isPinned && isHeader,
            'shadow-[inset_-1px_0_0_0_var(--table-border)]':
                isFirstHeaderColumn || isLastLeftPinnedColumn,
        };
    };

    const getBorderClasses = (column: Column<TData, any>) => {
        return {
            'border-secondary border-r': column.columnDef.meta?.border,
        };
    };

    const getFixedWidthPx = (columnId: string) => {
        const w = computedFixedSizes.value?.[columnId];
        if (typeof w === 'number' && w > 0) {
            return `${w}px`;
        }

        return undefined;
    };

    const autoComputedFixed = ref<Record<string, number>>({});

    const getHeaderElForColumn = (columnId: string): HTMLElement | null => {
        const container = (tableContainerRef.value as any)?.getScrollElement?.() ??
            (tableContainerRef.value?.$el as HTMLElement | null) ??
            document;

        return (container as HTMLElement).querySelector?.(
            `[data-header-id="${columnId}"]`,
        ) as HTMLElement | null;
    };

    const getCanvasFontFromElement = (el: HTMLElement | null): string => {
        const defaultFont = '12px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif';
        if (!el) {
            return defaultFont;
        }

        const cs = window.getComputedStyle(el);
        // Prefer computed shorthand font which includes all necessary parts
        const shorthand = (cs as any).font as string | undefined;
        if (shorthand && shorthand.length > 0) {
            return shorthand;
        }

        return `${cs.fontStyle} ${cs.fontVariant} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`.trim() || defaultFont;
    };

    const measureWithCanvas = (text: string, font: string): number => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return text.length * 8; // fallback heuristic
        }

        ctx.font = font;
        const metrics = ctx.measureText(text);

        return Math.ceil(metrics.width);
    };

    // Memoized status id → label maps per column
    const statusLabelMaps = computed<Record<string, Map<string, string>>>(() => {
        const meta = (table?.options?.meta || {}) as any;
        const result: Record<string, Map<string, string>> = {};
        const src = meta.statusOptions || {};
        for (const [colId, options] of Object.entries(src) as Array<[string, any[]]>) {
            const map = new Map<string, string>();
            if (Array.isArray(options)) {
                for (const opt of options) {
                    map.set(String((opt as any)?.value), String((opt as any)?.label ?? ''));
                }
            }
            result[colId] = map;
        }
        
        return result;
    });

    const computeAutoColumnWidths = () => {
        const buffers = props.autoComputeColumnSizes ?? {};
        const rowsModel = table.getRowModel().rows;

        for (const [columnId, bufferPx] of Object.entries(buffers)) {
            if (props.fixedColumnSizes && typeof props.fixedColumnSizes[columnId] === 'number') {
                continue;
            }

            const buffer = typeof bufferPx === 'number' ? bufferPx : 0;

            const col = table.getColumn(columnId);
            const headerEl = getHeaderElForColumn(columnId);
            const font = getCanvasFontFromElement(headerEl);

            let headerLabel: string | undefined;
            const rawHeader = col?.columnDef?.header as any;
            if (typeof rawHeader === 'string') {
                headerLabel = rawHeader;
            } else if (headerEl?.textContent) {
                headerLabel = headerEl.textContent.trim();
            }

            const headerTextWidth = headerLabel ? measureWithCanvas(headerLabel, font) : 0;
            // Include horizontal padding (~16px from px-2) + 16px for sort icons
            const headerBaseline = headerTextWidth + 16 + 16;

            // Measure all row values for the column via table row model
            let maxCellWidth = 0;
            const labelMap = statusLabelMaps.value[columnId];
            const getLabelForValue = (val: any): string => {
                if (labelMap && val != null) {
                    const mapped = labelMap.get(String(val));
                    if (mapped != null) {
                        
                        return mapped;
                    }
                }

                return val instanceof Date ? val.toLocaleString() : String(val ?? '');
            };
            for (const r of rowsModel) {
                const value = r.getValue(columnId) as any;
                const text = getLabelForValue(value);
                const w = measureWithCanvas(text, font);
                if (w > maxCellWidth) {
                    maxCellWidth = w;
                }
            }

            // Total width: max cell width + padding + buffer, but never less than header baseline or 90px
            const computedWidth = Math.max(maxCellWidth + 16 + buffer, headerBaseline, 90);

            autoComputedFixed.value = {
                ...autoComputedFixed.value,
                [columnId]: computedWidth,
            };
        }
    };

    const computedFixedSizes = computed<Record<string, number>>(() => {
        return {
            ...(props.fixedColumnSizes ?? {}),
            ...autoComputedFixed.value,
        };
    });

    const toggleChildrenSelection = (row: Row<TData>, value: any, table: TableType<TData>) => {
        const toggle = (r: Row<TData>) => {
            if (r.subRows?.length) {
                r.subRows.forEach(toggle);
            } else {
                table.getRowModel().rowsById[r.id]?.toggleSelected(!!value);
            }
        };
        if (row.subRows?.length) {
            row.subRows.forEach(toggle);
        }
    };

    const saveFiltersToLocalStorage = () => {
        if (props.tableKey) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(columnFilters.value));
        }
    };

    const { contains } = useFilter({ sensitivity: 'base' });

    const table = useVueTable({
        get data() {
            return props.data;
        },
        get columns() {
            return props.columns;
        },
        get rowCount() {
            if (props.manualPagination && props.totalRecords) {
                return props.totalRecords;
            }
        },

        manualPagination: props.manualPagination,
        manualSorting: props.manualSorting,
        manualFiltering: props.manualFiltering,

        enableColumnFilters: true,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getGroupedRowModel: getGroupedRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        filterFromLeafRows: true,
        autoResetExpanded: false,
        autoResetPageIndex: false,
        getSubRows: (row: any) => row.subRows,
        enableRowSelection: (row) => !row.subRows?.length,
        filterFns: {
            boolean: booleanFilterFn,
            valueInArray: valueInArrayFilterFn,
            dateRange: dateRangeFilterFn,
            periodRange: periodRangeFilterFn,
        },
        onSortingChange: (updaterOrValue) => {
            valueUpdater(updaterOrValue, sorting);
            emit('sorting-change', sorting.value);
        },
        onColumnFiltersChange: (updaterOrValue) => {
            valueUpdater(updaterOrValue, columnFilters);
            emit('filters-change', columnFilters.value);
            saveFiltersToLocalStorage();
            table.setPageIndex(0);
            if (props.autoComputeColumnSizes) {
                nextTick(() => {
                    computeAutoColumnWidths();
                });
            }
        },
        onColumnVisibilityChange: (updaterOrValue) =>
            valueUpdater(updaterOrValue, columnVisibility),
        onRowSelectionChange: (updaterOrValue) => valueUpdater(updaterOrValue, rowSelection),
        onExpandedChange: (updaterOrValue) => valueUpdater(updaterOrValue, expanded),
        onGlobalFilterChange: (updaterOrValue) => {
            valueUpdater(updaterOrValue, globalFilter);
            emit('global-filter-change', globalFilter.value);
            table.setPageIndex(0);
            if (props.autoComputeColumnSizes) {
                nextTick(() => {
                    computeAutoColumnWidths();
                });
            }
        },
        onPaginationChange: (updaterOrValue) => {
            valueUpdater(updaterOrValue, pagination);
            emit('pagination-change', pagination.value);
        },
        onColumnPinningChange: (updaterOrValue) => valueUpdater(updaterOrValue, columnPinning),
        onGroupingChange: (updaterOrValue) => valueUpdater(updaterOrValue, grouping),
        globalFilterFn: props.manualFiltering
            ? undefined
            : (row, columnId, filterValue) => {
                  let currentRow = row;

                  while (currentRow.parentId) {
                      const parentRow = table.getRow(currentRow.parentId);
                      if (contains(String(parentRow.getValue(columnId)), filterValue)) {
                          return true;
                      }
                      currentRow = parentRow;
                  }

                  return contains(String(row.getValue(columnId)), filterValue);
              },
        meta: props.meta,
        state: {
            get sorting() {
                return sorting.value;
            },
            get columnFilters() {
                return columnFilters.value;
            },
            get columnVisibility() {
                return columnVisibility.value;
            },
            get rowSelection() {
                return rowSelection.value;
            },
            get expanded() {
                return expanded.value;
            },
            get globalFilter() {
                return globalFilter.value;
            },
            get pagination() {
                return pagination.value;
            },
            get columnPinning() {
                return columnPinning.value;
            },
            get grouping() {
                return grouping.value;
            },
        },
    });

    const fallbackHydratingOverlayLeftPx = computed(() => {
        return (columnPinning.value.left ?? []).reduce((width, columnId) => {
            const fixedWidth = computedFixedSizes.value[columnId];

            if (typeof fixedWidth === 'number' && fixedWidth > 0) {
                return width + fixedWidth;
            }

            const column = table.getColumn(columnId);

            return width + (column?.getSize?.() ?? column?.columnDef.size ?? 0);
        }, 0);
    });

    const measuredHydratingOverlayLeftPx = ref<number | null>(null);

    const updateHydratingOverlayLeftPx = async () => {
        await nextTick();

        const scrollElement = (tableContainerRef.value as any)?.getScrollElement?.() as HTMLElement | null;
        const tableFrame = scrollElement?.parentElement;

        if (!scrollElement || !tableFrame) {
            measuredHydratingOverlayLeftPx.value = null;

            return;
        }

        const frameRect = tableFrame.getBoundingClientRect();
        const pinnedRightEdge = (columnPinning.value.left ?? []).reduce((rightEdge, columnId) => {
            const headerEl = getHeaderElForColumn(columnId);

            if (!headerEl) {
                return rightEdge;
            }

            const headerRect = headerEl.getBoundingClientRect();

            return Math.max(rightEdge, Math.ceil(headerRect.right - frameRect.left));
        }, 0);

        measuredHydratingOverlayLeftPx.value = pinnedRightEdge > 0 ? pinnedRightEdge : null;
    };

    const hydratingOverlayLeftPx = computed(() => (
        measuredHydratingOverlayLeftPx.value ?? fallbackHydratingOverlayLeftPx.value
    ));

    const hydratingOverlayStyle = computed(() => ({
        left: `${hydratingOverlayLeftPx.value}px`,
        right: '15px',
    }));

    const tableContainerRef = ref<ComponentPublicInstance | null>(null);
    const rowVirtualizerOptions = computed(() => {
        return {
            count: table.getRowModel().rows.length,
            estimateSize: () => props.virtualRowHeight, //estimate row height for accurate scrollbar dragging
            getItemKey: (index: number) => table.getRowModel().rows[index]?.id,
            getScrollElement: () => {
                const exposedGetter = (tableContainerRef.value as any)?.getScrollElement;
                if (typeof exposedGetter === 'function') {
                    return exposedGetter();
                }

                return (tableContainerRef.value?.$el as HTMLDivElement | null);
            },
            overscan: props.virtualOverscan,
        }
    })

const rowVirtualizer = useVirtualizer(rowVirtualizerOptions)

const rows = computed(() => table.getRowModel().rows)
const virtualRows = computed(() => rowVirtualizer.value.getVirtualItems())
const totalSize = computed(() => rowVirtualizer.value.getTotalSize())

const paddingTop = computed(() => {
    const v = virtualRows.value
    if (!v.length) {
        return 0
    }

    return v[0].start
})

const paddingBottom = computed(() => {
    const v = virtualRows.value
    if (!v.length) {
        return 0
    }

    const last = v[v.length - 1]

    return totalSize.value - last.end
})

    defineExpose({
        table,
    });

    onMounted(() => {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            columnFilters.value = JSON.parse(saved);
        }
        if (props.autoComputeColumnSizes) {
            computeAutoColumnWidths();
        }
        void updateHydratingOverlayLeftPx();
    });

    // Recompute on data changes (e.g., after async loads)
    watch(
        () => props.data,
        async () => {
            // Wait for DOM (header) to exist so we can read its computed font
            await nextTick();
            if (props.autoComputeColumnSizes) {
                computeAutoColumnWidths();
            }
            void updateHydratingOverlayLeftPx();
        },
        { deep: true }
    );

    watch(
        [computedFixedSizes, () => columnPinning.value.left, () => props.hydrating],
        () => {
            void updateHydratingOverlayLeftPx();
        },
        { deep: true },
    );
</script>

<style>
     @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
</style>

<template>
    <div class="flex h-full flex-col gap-4">
        <div
            v-if="enableToolbar"
            :class="cn('flex flex-wrap gap-4 sm:items-center', toolbarClass)"
        >
            <slot
                name="title"
                :table="table"
            />
            <div
                v-if="enableSearch || enableFilters || $slots.filters"
                class="flex items-center gap-2"
                :class="enableSearch && 'grow sm:max-w-sm'"
            >
                <Separator
                    orientation="vertical"
                    class="mr-2 hidden h-2.5! md:flex"
                />
                <DebouncedInput
                    v-if="enableSearch"
                    :placeholder="$t('datatables.search_placeholder')"
                    :modelValue="globalFilter ?? ''"
                    @update:modelValue="(value: string) => table.setGlobalFilter(String(value))"
                    :debounce="200"
                />
                <DataTableFilters
                    v-if="enableFilters"
                    :table="table"
                />
                <slot
                    name="filters"
                    :table="table"
                />
            </div>
            <Separator
                v-if="$slots.actions"
                orientation="vertical"
                class="hidden h-2.5! md:flex"
            />
            <slot
                name="actions"
                :table="table"
            />

            <DataTableGroupToggle :table="table" />
            <DataTableExportModel
                v-if="enableExport"
                :table="table"
            />
        </div>
        <slot
            name="table-top"
            :table="table"
        />
        <div class="flex min-h-0 flex-1 flex-col rounded border relative">
            <div 
                v-if="loading" 
                class="absolute bottom-[15px] inset-0 flex items-center justify-center pointer-events-none animate-[fadeIn_.3s_ease_both]"
            >
                <Loading />
            </div>
            <Table v-else ref="tableContainerRef">
                <TableHeader class="relative z-20">
                    <TableRow
                        v-for="headerGroup in table.getHeaderGroups()"
                        :key="headerGroup.id"
                    >
                        <TableHead
                            v-for="header in headerGroup.headers"
                            :key="header.id"
                            :class="[
                                getPinningClasses(header.column, true, headerGroup.depth),
                                getBorderClasses(header.column),
                            ]"
                            :style="{
                                width: getFixedWidthPx(header.column.id) ?? undefined,
                                minWidth: getFixedWidthPx(header.column.id) ?? undefined,
                                maxWidth: getFixedWidthPx(header.column.id) ?? undefined,
                                left:
                                    header.column.getIsPinned() === 'left' &&
                                    headerGroup ===
                                        table.getHeaderGroups()[table.getHeaderGroups().length - 1]
                                        ? `${header.column.getStart('left')}px`
                                        : undefined,
                                top: `${headerGroup.depth * 40}px`,
                            }"
                            :colSpan="header.colSpan"
                            :data-header-id="header.id"
                        >
                            <FlexRender
                                v-if="!header.isPlaceholder"
                                :render="header.column.columnDef.header"
                                :props="header.getContext()"
                            />
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    <template v-if="!virtualize && table.getRowModel().rows?.length">
                        <template
                            v-for="row in table.getRowModel().rows"
                            :key="row.id"
                        >
                            <TableRow
                                v-if="row.getCanExpand()"
                                :class="'bg-table-expanded-row font-medium'"
                                :data-state="row.getIsSelected() ? 'selected' : undefined"
                            >
                                <TableCell class="sticky left-0">
                                    <Checkbox
                                        :model-value="
                                            row.getIsSomeSelected()
                                                ? 'indeterminate'
                                                : row.getIsAllSubRowsSelected()
                                        "
                                        @update:model-value="
                                            (value) => toggleChildrenSelection(row, value, table)
                                        "
                                        aria-label="Select group"
                                    />
                                </TableCell>
                                <TableCell
                                    class="sticky cursor-pointer"
                                    :style="{ left: '24px' }"
                                    @click="row.getToggleExpandedHandler()()"
                                >
                                    <span class="flex items-center gap-2">
                                        {{ row.getValue('name') }} ({{ row.subRows?.length }})
                                        <Icon
                                            class="size-4 transition-transform duration-100"
                                            name="chevronRight"
                                            :class="row.getIsExpanded() ? 'rotate-90' : ''"
                                        />
                                    </span>
                                </TableCell>
                                <TableCell
                                    class="cursor-pointer"
                                    :colspan="
                                        row.getVisibleCells().length -
                                        row.getLeftVisibleCells().length
                                    "
                                    @click="row.getToggleExpandedHandler()()"
                                >
                                </TableCell>
                            </TableRow>

                            <TableRow
                                v-else
                                :data-state="row.getIsSelected() ? 'selected' : undefined"
                                :class="{ '[&_td]:bg-table-expanded-row': row.depth > 0 }"
                            >
                                <TableCell
                                    v-for="cell in row.getVisibleCells()"
                                    :key="cell.id"
                                    :class="[
                                        getPinningClasses(cell.column, false),
                                        ['actions', 'payslip_actions'].includes(cell.column.id)
                                            ? 'w-0 whitespace-nowrap'
                                            : '',
                                        cell.getIsGrouped() ? 'font-medium' : '',
                                        cell.getIsAggregated() ? 'text-muted-foreground' : '',
                                        getBorderClasses(cell.column),
                                    ]"
                                    :style="{
                                        width: getFixedWidthPx(cell.column.id) ?? undefined,
                                        minWidth: getFixedWidthPx(cell.column.id) ?? undefined,
                                        maxWidth: getFixedWidthPx(cell.column.id) ?? undefined,
                                        left: cell.column.getIsPinned() === 'left'
                                            ? `${cell.column.getStart('left')}px`
                                            : undefined,
                                    }"
                                >
                                    <template v-if="cell.getIsAggregated()">
                                        <FlexRender
                                            :render="
                                                cell.column.columnDef.aggregatedCell ||
                                                cell.column.columnDef.cell
                                            "
                                            :props="cell.getContext()"
                                        />
                                    </template>
                                    <template v-else>
                                        <FlexRender
                                            :render="cell.column.columnDef.cell"
                                            :props="cell.getContext()"
                                        />
                                    </template>
                                </TableCell>
                            </TableRow>
                        </template>
                    </template>

                    <template v-else-if="virtualize && table.getRowModel().rows?.length">
                        <TableRow v-if="paddingTop" aria-hidden="true" class="pointer-events-none">
                            <TableCell :colspan="columns.length" class="p-0">
                                <div :style="{ height: `${paddingTop}px` }" />
                            </TableCell>
                        </TableRow>

                        <template v-for="vRow in virtualRows" :key="rows[vRow.index].id">
                            <TableRow
                                v-if="rows[vRow.index].getCanExpand()"
                                :class="'bg-table-expanded-row font-medium animate-[fadeIn_.3s_ease_both]'"
                                :style="{ height: `${props.virtualRowHeight}px` }"
                                :data-state="rows[vRow.index].getIsSelected() ? 'selected' : undefined"
                            >
                                <TableCell class="sticky left-0">
                                    <Checkbox
                                        :model-value="
                                            rows[vRow.index].getIsSomeSelected()
                                                ? 'indeterminate'
                                                : rows[vRow.index].getIsAllSubRowsSelected()
                                        "
                                        @update:model-value="
                                            (value) => toggleChildrenSelection(rows[vRow.index], value, table)
                                        "
                                        aria-label="Select group"
                                    />
                                </TableCell>
                                <TableCell
                                    class="sticky cursor-pointer"
                                    :style="{ left: '24px' }"
                                    @click="rows[vRow.index].getToggleExpandedHandler()()"
                                >
                                    <span class="flex items-center gap-2">
                                        {{ rows[vRow.index].getValue('name') }} ({{ rows[vRow.index].subRows?.length }})
                                        <Icon
                                            class="size-4 transition-transform duration-100"
                                            name="chevronRight"
                                            :class="rows[vRow.index].getIsExpanded() ? 'rotate-90' : ''"
                                        />
                                    </span>
                                </TableCell>
                                <TableCell
                                    class="cursor-pointer"
                                    :colspan="
                                        rows[vRow.index].getVisibleCells().length -
                                        rows[vRow.index].getLeftVisibleCells().length
                                    "
                                    @click="rows[vRow.index].getToggleExpandedHandler()()"
                                >
                                </TableCell>
                            </TableRow>

                            <TableRow
                                v-else
                                :data-state="rows[vRow.index].getIsSelected() ? 'selected' : undefined"
                                :style="{ height: `${props.virtualRowHeight}px` }"
                                class="animate-[fadeIn_.3s_ease_both]"
                                :class="{ '[&_td]:bg-table-expanded-row': rows[vRow.index].depth > 0 }"
                            >
                                <TableCell
                                    v-for="cell in rows[vRow.index].getVisibleCells()"
                                    :key="cell.id"
                                    :class="[
                                        getPinningClasses(cell.column, false),
                                        ['actions', 'payslip_actions'].includes(cell.column.id)
                                            ? 'w-0 whitespace-nowrap'
                                            : '',
                                        cell.getIsGrouped() ? 'font-medium' : '',
                                        cell.getIsAggregated() ? 'text-muted-foreground' : '',
                                        getBorderClasses(cell.column),
                                    ]"
                                    :style="{
                                        width: getFixedWidthPx(cell.column.id) ?? undefined,
                                        minWidth: getFixedWidthPx(cell.column.id) ?? undefined,
                                        maxWidth: getFixedWidthPx(cell.column.id) ?? undefined,
                                        left: cell.column.getIsPinned() === 'left'
                                            ? `${cell.column.getStart('left')}px`
                                            : undefined,
                                    }"
                                >
                                    <template v-if="cell.getIsAggregated()">
                                        <FlexRender
                                            :render="
                                                cell.column.columnDef.aggregatedCell ||
                                                cell.column.columnDef.cell
                                            "
                                            :props="cell.getContext()"
                                        />
                                    </template>
                                    <template v-else>
                                        <template v-if="!props.hydrating || ['name','select'].includes(cell.column.id)">
                                            <FlexRender
                                            
                                                :render="cell.column.columnDef.cell"
                                                :props="cell.getContext()"
                                            />
                                        </template>
                                    </template>
                                </TableCell>
                            </TableRow>
                        </template>

                        <TableRow v-if="paddingBottom" aria-hidden="true" class="pointer-events-none">
                            <TableCell :colspan="columns.length" class="p-0">
                                <div :style="{ height: `${paddingBottom}px` }" />
                            </TableCell>
                        </TableRow>
                    </template>

                    <template v-else>
                        <TableRow>
                            <TableCell
                                :colspan="columns.length"
                                class="h-24 text-center"
                            >
                                <div class="flex flex-col items-center gap-2">
                                    <Icon
                                        v-if="isError"
                                        name="alertCircle"
                                        class="text-destructive size-6"
                                    />
                                    <span>{{ $t('datatables.infoEmpty') }}</span>
                                </div>
                            </TableCell>
                        </TableRow>
                    </template>
                </TableBody>
            </Table>
            <div 
                v-if="!loading && props.hydrating" 
                class="absolute bottom-[15px] inset-0 z-10 bg-white/60 backdrop-blur-sm flex items-center justify-center pointer-events-none animate-[fadeIn_.3s_ease_both]"
                :style="hydratingOverlayStyle"
            >
                <Loading />
            </div>
        </div>

        <DataTableOptions
            :table="table"
            :manual-pagination="props.manualPagination"
            :total-records="totalRecords"
            :hide-controls="props.hideControls"
        />
    </div>
</template>
